import { useState } from "react";
import { X } from "lucide-react";
import { ApiError } from "../api/client";
import type { ChainId, TaskPoolRef, Token } from "../api/types";
import { PoolPicker } from "./PoolPicker";

type Props = {
  mode: "create" | "edit";
  initial?: Token;
  onCancel: () => void;
  onSubmit: (body: unknown) => Promise<void>;
};

export function TokenEditor({ mode, initial, onCancel, onSubmit }: Props): JSX.Element {
  const [chain, setChain] = useState<ChainId>(initial?.chain ?? "solana");
  const [name, setName] = useState(initial?.name ?? "");
  const [mint, setMint] = useState(initial?.mint ?? "");
  const [pools, setPools] = useState<TaskPoolRef[]>(initial?.pools ?? []);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEvm = chain === "base" || chain === "ethereum" || chain === "robinhood" || chain === "bnb" || chain === "ink" || chain === "arc";
  // Kept for label/placeholder copy; "Base" branding doesn't matter on
  // Ethereum forms — placeholder examples differ per chain below.
  const isBase = chain === "base";

  async function submit(): Promise<void> {
    setError(null);
    if (mode === "create") {
      if (!name.trim()) return setError("Name required");
      if (isEvm) {
        if (!/^0x[a-fA-F0-9]{40}$/.test(mint.trim())) {
          return setError(`${chain} token address must be 0x-prefixed 40-hex`);
        }
      } else {
        if (mint.trim().length < 32) {
          return setError("Solana mint must be a base58 address (32+ chars)");
        }
      }
      if (pools.length === 0) return setError("Add at least one pool");
    } else {
      if (!name.trim()) return setError("Name required");
      if (pools.length === 0) return setError("Token must have at least one pool");
    }

    const body =
      mode === "create"
        ? { chain, name: name.trim(), mint: mint.trim(), pools }
        : { name: name.trim(), pools };

    setSubmitting(true);
    try {
      await onSubmit(body);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Save failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop fixed inset-0 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-12">
      <div className="w-full max-w-2xl bg-bg border border-fg mx-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="font-serif text-xl">
            {mode === "create" ? "Add token" : `Edit token — ${initial?.name}`}
          </h3>
          <button onClick={onCancel} className="text-muted hover:text-fg">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Chain</label>
              <select
                className="input"
                value={chain}
                onChange={(e) => {
                  const next = e.target.value as ChainId;
                  setChain(next);
                  setMint("");
                  setPools([]);
                }}
                disabled={mode === "edit"}
                title={mode === "edit" ? "Chain is locked after creation" : undefined}
              >
                <option value="solana">Solana</option>
                <option value="base">Base</option>
                <option value="ethereum">Ethereum</option>
                <option value="robinhood">Robinhood</option>
                <option value="bnb">BNB Smart Chain</option>
                <option value="ink">Ink</option>
                <option value="arc">Arc / Lift.fun + Full.fun</option>
              </select>
            </div>
            <div>
              <label className="label">Name</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={chain === "ethereum" ? "PEPE" : chain === "robinhood" ? "RH TOKEN" : chain === "bnb" ? "CAKE" : chain === "ink" ? "INKY" : chain === "arc" ? "LIFT" : isBase ? "BRETT" : "MEOW"}
              />
            </div>
          </div>

          <div>
            <label className="label">
              {isEvm ? "Token contract (0x…)" : "Token mint (CA)"}
            </label>
            <input
              className="input font-mono"
              value={mint}
              onChange={(e) => setMint(e.target.value)}
              placeholder={
                chain === "ethereum"
                  ? "0x6982508145454ce325ddbe47a25d4ec3d2311933"
                  : chain === "robinhood"
                    ? "0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168"
                  : chain === "bnb"
                    ? "0x0E09fABB73Bd3Ade0a17ECC321fD13a19e81cE82"
                  : chain === "ink"
                    ? "0xc1ad67688b9afd7cd7cdfc9bd6997f4e87ab0343"
                  : chain === "arc"
                    ? "0xf534d23ae6a291d68dea52ac27dc3e89393d0087"
                  : isBase
                    ? "0x532f27101965dd16442E59d40670FaF5eBB142E4"
                    : "8J69rbLTzWWgUJziFY8jeu5tDwEPBwUz4pKBMr5rpump"
              }
              disabled={mode === "edit"}
            />
          </div>

          <div>
            <label className="label">Pools</label>
            <PoolPicker mint={mint} chain={chain} selected={pools} onChange={setPools} />
          </div>

          {error && (
            <div className="text-xs text-err uppercase tracking-nav">{error}</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onCancel} className="btn btn-ghost">
            Cancel
          </button>
          <button
            onClick={() => void submit()}
            disabled={submitting}
            className="btn btn-primary disabled:opacity-50"
          >
            {submitting ? "Saving…" : mode === "create" ? "Save token" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
